import { FunctionComponent, useCallback } from "react";
import { MenuItem, Button as MUIButton } from "@mui/material";
import {
  DataGridPro,
  GridFooter,
  GridFooterContainer,
} from "@mui/x-data-grid-pro";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import { styled } from "@mui/material/styles";
import Grid from "@mui/material/Unstable_Grid2";
import { IProps } from "./IProps";
import { Button } from "../Button";
import { CustomSelect } from "../CustomSelect";
import { CustomInput } from "../CustomInput";
import { useCustomDatagridPro, useDatagridProPagination } from "./hook";

const StyledDataGrid = styled(DataGridPro)<IProps>(({ theme }) => ({
  "& .MuiDataGrid-columnHeader": {
    backgroundColor: "#1BA0E9",
    color: "#FFFFFF",
    fontSize: 14,
  },
  "& .MuiDataGrid-columnSeparator": {
    visibility: "visible",
  },
  "&.MuiDataGrid-root .MuiDataGrid-columnHeader:focus, &.MuiDataGrid-root .MuiDataGrid-cell:focus":
    {
      outline: "transparent",
    },
  "& .MuiDataGrid-row": {
    fontSize: 14,
  },
  "& .MuiDataGrid-virtualScrollerRenderZone": {
    "& .MuiDataGrid-row": {
      "&:nth-of-type(2n)": {
        backgroundColor: "#FAFAFA",
      },
      "&:hover": {
        backgroundColor: "#DDF1FC",
        borderBottomColor: "#8DD0F4",
      },
      "&:last-child": {
        boxShadow:
          "0px 0px 2px rgba(40, 72, 154, 0.1), 0px 3px 6px rgba(40, 72, 154, 0.1)",
      },
    },
  },
  ".MuiDataGrid-row:hover": {
    backgroundColor: "#DDF1FC",
    borderBottomColor: "#8DD0F4",
  },
}));

export const CustomDataGridPro: FunctionComponent<IProps> = (props) => {
  const {
    page,
    pageSize,
    orderBy,
    onChangeOrderBy,
    onPageChange,
    onPageSizeChange,
  } = useDatagridProPagination();

  const {
    isLoading,
    columns,
    rows,
    rowModesModel,
    totalRecordCount,
    buttons,
    filter,
    search,
    dropdownItems,
    isFilterEnabled,
    isSearchEnabled,
    getRows,
    onChange,
    onChangeSelect,
    handleRowEditStart,
    handleRowEditStop,
    onRowModesModelChange,
    processRowUpdate,
    onHandleAddClick,
  } = useCustomDatagridPro(props.tableName, page, pageSize, orderBy);

  const handleSearchApply = useCallback(async () => {
    await getRows();
  }, [getRows]);

  const CustomFooter = () => {
    return (
      <GridFooterContainer>
        {props.canAddRows && (
          <MUIButton
            sx={{
              textTransform: "none",
              fontWeight: 700,
              color: "#1BA0E9",
              marginLeft: 2,
            }}
            variant="text"
            startIcon={<AddIcon />}
            onClick={onHandleAddClick}
          >
            Add Row
          </MUIButton>
        )}
        <GridFooter sx={{ border: "none" }} />
      </GridFooterContainer>
    );
  };

  return (
    <>
      <Grid columns={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
        <Grid
          sm={12}
          md={12}
          lg={12}
          container
          columnSpacing={{ xs: 0, md: 1 }}
          rowSpacing={{ xs: 1 }}
          width="100%"
          sx={{ mb: 2.5 }}
        >
          <Grid
            xs={12}
            sm={12}
            md={6}
            lg={5}
            sx={{ width: "100%" }}
            display={isFilterEnabled ? "block" : "none"}
          >
            <CustomSelect
              width="100%"
              name="type"
              label="Filter"
              value={filter}
              onChange={onChangeSelect}
            >
              {dropdownItems?.map((item, index) => (
                <MenuItem key={index} value={item.id}>
                  {item.description}
                </MenuItem>
              ))}
            </CustomSelect>
          </Grid>
          <Grid
            xs={12}
            sm={12}
            md={4}
            lg={5}
            display={isSearchEnabled ? "flex" : "none"}
          >
            <CustomInput
              startAdornment={<SearchIcon />}
              width={350}
              height={35}
              value={search}
              onChange={onChange}
              label={{ text: "Search", color: "rgba(0, 0, 0, 0.8)" }}
              hint={{
                type: "info",
                message: "Click on a header to get results based on that",
              }}
            />
            <Grid
              alignSelf="flex-start"
              pt={3.5}
              xs={12}
              sm={3}
              md={2}
              lg={2}
              width="100%"
              display={isSearchEnabled ? "flex" : "none"}
              alignItems="flex-end"
            >
              <Button
                width={50}
                height={36}
                onClick={handleSearchApply}
                label="Apply"
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid
          container
          xs={12}
          sm={12}
          lg={12}
          columnSpacing={{ xs: 0, sm: 3 }}
          rowSpacing={{ xs: 3 }}
          width="100%"
          sx={{ mb: 1.25 }}
        >
          <Grid width="100%" xs={12} sm={4} md={4} lg={3}>
            {buttons?.map((item, index) => (
              <Button
                sx={{
                  paddingTop: 1,
                  paddingLeft: 2.5,
                  paddingBottom: 1,
                  paddingRight: 2.5,
                }}
                width="100%"
                height={35}
                key={index}
                value={item.name}
                label={item.heading}
                variant="outlined"
                onClick={() => null}
              />
            ))}
          </Grid>
        </Grid>
      </Grid>
      <StyledDataGrid
        initialState={{
          sorting: {
            sortModel: [],
          },
          pinnedColumns: { right: ["actions"] },
        }}
        tableName={props.tableName}
        TopLeftElement={props.TopLeftElement}
        sx={{
          width: "100%",
          borderRadius: 2.5,
          boxShadow:
            "0px 0px 4px rgba(40, 72, 154, 0.04), 0px 8px 16px rgba(40, 72, 154, 0.08)",
        }}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        components={{ Footer: CustomFooter }}
        rowHeight={65}
        columns={columns}
        apiRef={undefined}
        rows={rows}
        sortModel={orderBy}
        sortingOrder={["desc", "asc"]}
        page={page}
        rowsPerPageOptions={[5, 10, 15, 25]}
        pageSize={pageSize}
        loading={isLoading}
        getRowId={(row) => row.id}
        pagination
        editMode="row"
        paginationMode="server"
        sortingMode="server"
        rowModesModel={rowModesModel}
        onRowModesModelChange={onRowModesModelChange}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        rowCount={totalRecordCount}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        onSortModelChange={onChangeOrderBy}
        processRowUpdate={processRowUpdate}
        experimentalFeatures={{ newEditingApi: true }}
      />
    </>
  );
};
